import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CalendarClock, User, MapPin, GraduationCap, Clock, Search } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  listMyStudentSchedules, listMyTcSchedules, listMyEnrollableStudents, selfEnrollMyStudent,
} from "@/lib/api/parent-invites.functions";
import { LoadErrorState } from "@/components/parent/LoadErrorState";
import { describeLoadError } from "@/lib/browser-storage";

export const Route = createFileRoute("/_authenticated/responsavel/treinamento")({
  component: ParentTrainingPage,
});

const DAY_LABELS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const DAY_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

type TcSchedule = {
  id: string;
  name: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  category: string | null;
  location: string | null;
  allow_self_enroll?: boolean | null;
  training_types: { name: string } | null;
  rental_modalities: { name: string } | null;
};

type Grouped = {
  key: string;
  name: string;
  start_time: string;
  end_time: string;
  category: string | null;
  location: string | null;
  typeName: string | null;
  days: number[];
  ids: string[];
  allowSelfEnroll: boolean;
};

function ParentTrainingPage() {
  const listMine = useServerFn(listMyStudentSchedules);
  const listAll = useServerFn(listMyTcSchedules);
  const [mine, setMine] = useState<any[]>([]);
  const [all, setAll] = useState<TcSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [studentFilter, setStudentFilter] = useState<string>("all");
  const [dayFilter, setDayFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [onlyMine, setOnlyMine] = useState<string>("all");
  const listEnrollable = useServerFn(listMyEnrollableStudents);
  const enroll = useServerFn(selfEnrollMyStudent);
  const [enrollable, setEnrollable] = useState<{ id: string; name: string; hasPlan: boolean; canSelfEnroll: boolean }[]>([]);
  const [enrolling, setEnrolling] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [a, b] = await Promise.allSettled([listMine(), listAll()]);
    if (a.status === "fulfilled") setMine((a.value as any).schedules ?? []);
    if (b.status === "fulfilled") setAll(((b.value as any).schedules ?? []) as TcSchedule[]);
    try {
      const e = await listEnrollable();
      setEnrollable(((e as any).students ?? []) as any);
    } catch { /* ignore */ }
    if (a.status === "rejected" && b.status === "rejected") {
      setError(a.reason ?? b.reason);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const students = useMemo(() => {
    const map = new Map<string, string>();
    mine.forEach((s) => {
      if (s.students?.id) map.set(s.students.id, s.students.name || "Aluno");
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [mine]);

  const mineFiltered = useMemo(() => {
    if (studentFilter === "all") return mine;
    return mine.filter((s) => s.students?.id === studentFilter);
  }, [mine, studentFilter]);

  const myScheduleIds = useMemo(
    () => new Set(mineFiltered.map((s) => s.training_schedules?.id).filter(Boolean)),
    [mineFiltered],
  );

  const weekMap = useMemo(() => {
    const m: Record<number, any[]> = {};
    mineFiltered.forEach((s) => {
      const dow = s.training_schedules?.day_of_week ?? 0;
      (m[dow] ||= []).push(s);
    });
    return m;
  }, [mineFiltered]);

  const selfEnrollStudents = useMemo(() => enrollable.filter((s) => s.canSelfEnroll), [enrollable]);

  // Determina o aluno-alvo para a auto-matrícula considerando o filtro atual.
  const enrollTarget = useMemo(() => {
    if (studentFilter !== "all") {
      return enrollable.find((s) => s.id === studentFilter) ?? null;
    }
    return selfEnrollStudents[0] ?? enrollable[0] ?? null;
  }, [studentFilter, enrollable, selfEnrollStudents]);

  // Motivo exato pelo qual a auto-matrícula está bloqueada (null = liberada).
  const enrollBlockReason = useMemo(() => {
    if (enrollable.length === 0) return "Nenhum aluno cadastrado neste centro de treinamento.";
    if (!enrollTarget) return "Selecione um aluno para matricular.";
    if (!enrollTarget.hasPlan) return "Aluno sem plano de pagamento ativo.";
    if (!enrollTarget.canSelfEnroll) {
      return "O plano do aluno não permite auto-matrícula pelo aplicativo. Fale com o Centro de Treinamento.";
    }
    return null;
  }, [enrollable, enrollTarget]);

  const doEnroll = async (g: Grouped) => {
    if (enrollBlockReason || !enrollTarget) {
      toast.error(enrollBlockReason ?? "Não foi possível identificar o aluno para matrícula.");
      return;
    }
    setEnrolling(g.key);
    try {
      await enroll({ data: { studentId: enrollTarget.id, scheduleIds: g.ids } });
      toast.success(`${enrollTarget.name} matriculado(a) em ${g.name}.`);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível matricular.");
    } finally {
      setEnrolling(null);
    }
  };

  const grouped: Grouped[] = useMemo(() => {
    const map = new Map<string, Grouped>();
    for (const r of all) {
      const key = `${r.name}|${r.start_time}|${r.end_time}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: r.name,
          start_time: r.start_time,
          end_time: r.end_time,
          category: r.category,
          location: r.rental_modalities?.name ?? r.location,
          typeName: r.training_types?.name ?? null,
          days: [],
          ids: [],
          allowSelfEnroll: r.allow_self_enroll === true,
        });
      }
      const g = map.get(key)!;
      if (!g.days.includes(r.day_of_week)) g.days.push(r.day_of_week);
      if (!g.ids.includes(r.id)) g.ids.push(r.id);
    }
    return Array.from(map.values()).sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [all]);

  const categories = useMemo(
    () => Array.from(new Set(all.map((s) => s.category).filter(Boolean))) as string[],
    [all],
  );

  const visibleGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return grouped.filter((g) => {
      if (dayFilter !== "all" && !g.days.includes(Number(dayFilter))) return false;
      if (categoryFilter !== "all" && (g.category ?? "") !== categoryFilter) return false;
      if (onlyMine === "mine" && !g.ids.some((id) => myScheduleIds.has(id))) return false;
      if (q && !`${g.name} ${g.category ?? ""} ${g.location ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [grouped, dayFilter, categoryFilter, onlyMine, myScheduleIds, search]);

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>;
  }

  if (error) {
    return (
      <div className="p-4 max-w-3xl mx-auto">
        <LoadErrorState
          rawError={error}
          message={describeLoadError(error, "Erro ao carregar treinos.")}
          onRetry={() => void load()}
        />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarClock className="w-6 h-6 text-primary" /> Horário de Treinos</h1>
          <p className="text-sm text-muted-foreground">Consulta das turmas disponíveis (somente leitura).</p>
        </div>
        {students.length > 1 && (
          <Select value={studentFilter} onValueChange={setStudentFilter}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Filtrar por aluno" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os alunos</SelectItem>
              {students.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {mineFiltered.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="text-xs font-semibold text-muted-foreground mb-2">Visão da semana (meus treinos)</div>
            <div className="grid grid-cols-7 gap-1">
              {DAY_SHORT.map((label, dow) => {
                const items = weekMap[dow] ?? [];
                return (
                  <div key={dow} className={`rounded-md border p-2 min-h-[70px] text-[11px] ${items.length ? "bg-primary/5 border-primary/30" : "bg-muted/30"}`}>
                    <div className="font-semibold text-center mb-1">{label}</div>
                    {items.slice(0, 3).map((s) => (
                      <div key={s.id} className="truncate text-muted-foreground">
                        {s.training_schedules?.start_time?.slice(0, 5)}
                      </div>
                    ))}
                    {items.length > 3 && <div className="text-center text-muted-foreground">+{items.length - 3}</div>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros das turmas */}
      <Card>
        <CardContent className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Buscar turma..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={dayFilter} onValueChange={setDayFilter}>
            <SelectTrigger><SelectValue placeholder="Dia da semana" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os dias</SelectItem>
              {DAY_LABELS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as categorias</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={onlyMine} onValueChange={setOnlyMine}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as turmas</SelectItem>
              <SelectItem value="mine">Somente as minhas</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {visibleGroups.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Nenhuma turma encontrada com os filtros selecionados.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleGroups.map((g) => {
            const isMine = g.ids.some((id) => myScheduleIds.has(id));
            return (
              <Card key={g.key} className={isMine ? "border-primary/50" : undefined}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold truncate">{g.name}</h3>
                    {isMine && <Badge className="shrink-0">Minha turma</Badge>}
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4" /> {g.start_time.slice(0, 5)} — {g.end_time.slice(0, 5)}
                  </div>
                  {g.location && (
                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> {g.location}
                    </div>
                  )}
                  {g.allowSelfEnroll && !isMine && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block">
                            <Button
                              size="sm"
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                              disabled={enrolling === g.key || !!enrollBlockReason}
                              onClick={() => void doEnroll(g)}
                            >
                              {enrolling === g.key ? "Matriculando…" : "Matricular nesta turma"}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {enrollBlockReason && (
                          <TooltipContent>
                            <p className="max-w-[220px] text-xs">{enrollBlockReason}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {!g.allowSelfEnroll && !isMine && (
                    <p className="text-[11px] text-muted-foreground">
                      Matrícula feita pelo centro de treinamento.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {DAY_SHORT.map((label, i) => (
                      <span
                        key={i}
                        className={`text-xs px-2 py-1 rounded-md border ${g.days.includes(i) ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground"}`}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                  {(g.category || g.typeName) && (
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 pt-2 border-t">
                      {g.category && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {g.category}</span>}
                      {g.typeName && <span className="flex items-center gap-1"><GraduationCap className="w-3 h-3" /> {g.typeName}</span>}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
